import { useMemo, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { languageName } from "@mip-tg-bot-turntable/platform-api";
import { adminErrText, adminGet, adminSend, AdminApiError } from "../../api.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TEXT_KEY_ZH, textKeyZh } from "../../lib/textKeys.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";
import { useClientPagination } from "../../hooks/useClientPagination.ts";

// 文案 key 下拉选项：中文描述 + key，按描述排序。
const TEXT_KEY_OPTIONS = Object.keys(TEXT_KEY_ZH)
  .map((k) => ({ value: k, label: `${textKeyZh(k)}（${k}）` }))
  .sort((a, b) => a.label.localeCompare(b.label, "zh"));

interface TextRow {
  id: string;
  key: string;
  language_code: string;
  text: string;
  updated_at: string;
}

function extractPlaceholders(text: string): string[] {
  const matches = text.match(/\{\{([^}]+)\}\}/g) ?? [];
  const unique = new Set(matches.map((m) => m.slice(2, -2).trim()));
  return Array.from(unique);
}

export function TextsTab(props: { readonly botId: string; readonly languages: readonly string[] }) {
  const { botId } = props;
  const qc = useQueryClient();
  // 语言下拉：本 bot 启用语言 + default（兜底桶）。
  const langSelectData = [
    ...props.languages.map((cd) => ({ value: cd, label: languageName(cd) })),
    { value: "default", label: "默认 / 兜底 (default)" },
  ];
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TextRow | null>(null);
  const [previewRow, setPreviewRow] = useState<TextRow | null>(null);
  const [previewLang, setPreviewLang] = useState("en-IN");
  const [previewVars, setPreviewVars] = useState('{"bot_name":"MIP","spins":3}');
  const [langFilter, setLangFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const createForm = useForm<{ key: string; languageCode: string; text: string }>({
    initialValues: { key: "", languageCode: "en-IN", text: "" },
    validate: {
      key: (v) => (v.trim().length === 0 ? "必填" : null),
      text: (v) => (v.trim().length === 0 ? "必填" : null),
    },
  });

  const editForm = useForm<{ text: string }>({
    initialValues: { text: "" },
    validate: { text: (v) => (v.trim().length === 0 ? "必填" : null) },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "texts"],
    queryFn: () => adminGet<{ items: TextRow[] }>(`/bots/${botId}/texts`),
  });
  const create = useMutation({
    mutationFn: (v: { key: string; languageCode: string; text: string }) =>
      adminSend("POST", `/bots/${botId}/texts`, v),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已保存" });
      setCreateOpen(false);
      createForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "texts"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const update = useMutation({
    mutationFn: (args: { id: string; text: string }) =>
      adminSend("PATCH", `/bots/${botId}/texts/${args.id}`, { text: args.text }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "texts"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/bots/${botId}/texts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["bot", botId, "texts"] }),
  });
  const preview = useMutation({
    mutationFn: (args: { key: string; languageCode: string; vars: Record<string, unknown> }) =>
      adminSend<{ rendered: string }>("POST", `/bots/${botId}/texts/preview`, args),
    onSuccess: (r) => {
      notifications.show({ color: "blue", title: "渲染结果", message: r.rendered });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  // group by key；按语言/关键字筛 entry 后再分页
  const allItems = list.data?.items ?? [];
  const keyword = search.trim().toLowerCase();
  const groupedEntries = useMemo(() => {
    const grouped = new Map<string, TextRow[]>();
    for (const row of allItems) {
      if (langFilter !== "all" && row.language_code !== langFilter) continue;
      if (keyword !== "") {
        const keyHit =
          row.key.toLowerCase().includes(keyword) ||
          textKeyZh(row.key).toLowerCase().includes(keyword);
        const textHit = row.text.toLowerCase().includes(keyword);
        if (!keyHit && !textHit) continue;
      }
      const existing = grouped.get(row.key);
      if (existing !== undefined) {
        existing.push(row);
      } else {
        grouped.set(row.key, [row]);
      }
    }
    return Array.from(grouped.entries());
  }, [allItems, langFilter, keyword]);
  const resetKey = `${langFilter}|${keyword}`;
  const {
    pageItems: pagedGroups,
    page,
    setPage,
    pageSize,
    total,
    totalPages,
  } = useClientPagination(groupedEntries, resetKey);

  const editingPlaceholders = editing !== null ? extractPlaceholders(editing.text) : [];

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="lg">
            本 Bot 文案覆盖
          </Text>
          <Button onClick={() => setCreateOpen(true)}>新增覆盖</Button>
        </Group>
        <Text size="xs" c="dimmed" mb="md">
          仅对当前 Bot 生效，优先级高于全局库。未覆盖的 key
          自动使用左侧「文案库」的全局默认；全局共享文案请在「文案库」维护。
        </Text>
        <Group gap="sm" mb="md" wrap="wrap">
          <Select
            size="xs"
            label="语言"
            data={[{ value: "all", label: "全部" }, ...langSelectData]}
            value={langFilter}
            onChange={(v) => setLangFilter(v ?? "all")}
            style={{ width: 180 }}
            clearable={false}
            searchable
          />
          <TextInput
            size="xs"
            label="搜索"
            placeholder="key / 文案"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ minWidth: 220, flex: 1 }}
          />
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} h={48} radius="sm" />
            ))}
          </Stack>
        ) : allItems.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            暂无文案配置
          </Text>
        ) : groupedEntries.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            无匹配文案
          </Text>
        ) : (
          <>
            <Accordion variant="contained">
              {pagedGroups.map(([key, rows]) => (
                <Accordion.Item key={key} value={key}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {textKeyZh(key)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {key}
                      </Text>
                      <Badge color="gray" size="xs" variant="light">
                        {rows.length} 种语言
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <AdminTable minWidth={400}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>语言</Table.Th>
                          <Table.Th>文本</Table.Th>
                          <Table.Th>更新时间</Table.Th>
                          <Table.Th>操作</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {rows.map((row) => (
                          <Table.Tr key={row.id}>
                            <Table.Td>{row.language_code}</Table.Td>
                            <Table.Td
                              style={{
                                maxWidth: 280,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.text.slice(0, 60)}
                            </Table.Td>
                            <Table.Td>{row.updated_at}</Table.Td>
                            <Table.Td>
                              <Group gap={4}>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => {
                                    editForm.setValues({ text: row.text });
                                    setEditing(row);
                                  }}
                                >
                                  编辑
                                </Button>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => {
                                    setPreviewRow(row);
                                    setPreviewLang(row.language_code);
                                  }}
                                >
                                  预览
                                </Button>
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="outline"
                                  onClick={() => {
                                    modals.openConfirmModal({
                                      title: "确认删除",
                                      children: `确认删除文案「${textKeyZh(row.key)}」（${row.language_code}）？`,
                                      labels: { confirm: "删除", cancel: "取消" },
                                      confirmProps: { color: "red" },
                                      onConfirm: () => remove.mutate(row.id),
                                    });
                                  }}
                                >
                                  删除
                                </Button>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </AdminTable>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal
        title="新增 / 覆盖文案"
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        keepMounted={false}
      >
        <form onSubmit={createForm.onSubmit((v) => create.mutate(v))}>
          <Stack gap="sm">
            <Select
              label="文案 key"
              placeholder="按用途选择要新增 / 覆盖的文案"
              data={TEXT_KEY_OPTIONS}
              searchable
              required
              {...createForm.getInputProps("key")}
            />
            <Select
              label="语言"
              data={langSelectData}
              searchable
              {...createForm.getInputProps("languageCode")}
            />
            <Textarea label="文本" rows={4} required {...createForm.getInputProps("text")} />
            <Button type="submit" loading={create.isPending}>
              保存
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        title={`编辑文案：${editing !== null ? textKeyZh(editing.key) : ""}`}
        opened={editing !== null}
        onClose={() => setEditing(null)}
        keepMounted={false}
        size="lg"
      >
        <form onSubmit={editForm.onSubmit((v) => update.mutate({ id: editing!.id, text: v.text }))}>
          <Stack gap="sm">
            <TextInput
              label="文案 key"
              value={editing !== null ? `${textKeyZh(editing.key)}（${editing.key}）` : ""}
              disabled
            />
            <TextInput label="语言" value={editing?.language_code ?? ""} disabled />
            {editingPlaceholders.length > 0 ? (
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  可用占位符：
                </Text>
                <Group gap="xs">
                  {editingPlaceholders.map((ph) => (
                    <Badge key={ph} color="blue" variant="light" size="sm">
                      {`{{${ph}}}`}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            ) : null}
            <Textarea label="文本" rows={6} required {...editForm.getInputProps("text")} />
            <Button type="submit" loading={update.isPending}>
              保存
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        title={`预览 ${previewRow !== null ? textKeyZh(previewRow.key) : ""}`}
        opened={previewRow !== null}
        onClose={() => setPreviewRow(null)}
        keepMounted={false}
      >
        <Stack gap="sm">
          <TextInput
            label="语言"
            value={previewLang}
            onChange={(e) => setPreviewLang(e.currentTarget.value)}
          />
          <Textarea
            label="变量 (JSON 对象)"
            rows={3}
            value={previewVars}
            onChange={(e) => setPreviewVars(e.currentTarget.value)}
          />
          <Button
            loading={preview.isPending}
            onClick={() => {
              try {
                const vars = JSON.parse(previewVars) as Record<string, unknown>;
                preview.mutate({
                  key: previewRow!.key,
                  languageCode: previewLang,
                  vars,
                });
              } catch {
                notifications.show({ color: "red", message: "变量 JSON 格式有误" });
              }
            }}
          >
            渲染
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
